import type { StatusBreakdownEntry } from "#/types/report";
import { formatCurrency } from "#/utils/formatCurrency";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import DonutChart from "#/components/ui/DonutChart";
import { EmptyState } from "#/components/ui/EmptyState";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import InfoTooltip from "#/components/ui/InfoTooltip";
import ReportSection from "./ReportSection";

const STATUS_COLORS: Record<string, string> = {
  on_site: "var(--mantine-color-blue-5)",
  in_stock: "var(--mantine-color-green-5)",
  in_stock_main: "var(--mantine-color-green-6)",
  in_stock_sub: "var(--mantine-color-green-4)",
  in_delivery: "var(--mantine-color-yellow-5)",
  in_transit_to_sub: "var(--mantine-color-yellow-6)",
  inbound_sub: "var(--mantine-color-yellow-4)",
  issued_to_field: "var(--mantine-color-orange-5)",
  in_retrieval: "var(--mantine-color-violet-5)",
  in_maintenance: "var(--mantine-color-indigo-5)",
  missing: "var(--mantine-color-red-5)",
  incomplete: "var(--mantine-color-red-4)",
  delivered: "var(--mantine-color-teal-5)",
  storm_stock: "var(--mantine-color-gray-6)",
};

const DEFAULT_COLOR = "var(--mantine-color-gray-5)";
const OTHERS_COLOR = "var(--mantine-color-gray-3)";

// Card tints derived from the exact donut slice colour, so each card matches its
// slice (including main/sub shade differences).
const StatCard = ({
  label,
  count,
  value,
  color,
}: {
  label: string;
  count: number;
  value: number;
  color: string;
}) => (
  <div
    className="flex flex-col gap-1 rounded-lg border border-l-4 p-3"
    style={{
      borderColor: `color-mix(in srgb, ${color} 24%, white)`,
      borderLeftColor: color,
      backgroundColor: `color-mix(in srgb, ${color} 10%, white)`,
    }}
  >
    <div className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </span>
    </div>
    <span
      className="text-2xl font-extrabold"
      style={{ color: `color-mix(in srgb, ${color} 82%, black)` }}
    >
      {count.toLocaleString()}
    </span>
    <span className="text-xs text-gray-500">
      Est. value: <span className="font-medium text-gray-700">{formatCurrency(value)}</span>
    </span>
  </div>
);

interface StatusOverviewProps {
  /** Total across all allowed statuses (unaffected by the dropdown selection). */
  total: { count: number; value: number };
  /** Full status breakdown (all allowed statuses). */
  data: StatusBreakdownEntry[];
  /** Statuses currently selected in the page filter. */
  selected: string[];
  totalTooltip?: ReactNode;
  tooltip?: ReactNode;
  /** Rendered on the right of the section title (the status filter dropdown). */
  rightSection?: ReactNode;
}

const StatusOverview = ({
  total,
  data,
  selected,
  totalTooltip,
  tooltip,
  rightSection,
}: StatusOverviewProps) => {
  const { t } = useTranslation();

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visibleEntries = data.filter((entry) => selectedSet.has(entry.status));

  // Fill vertically first (up to 3 rows) so few cards stack into a column and
  // fill the left column's height; more cards add columns. Capped at 5 per row.
  const cardColumns = useMemo(() => {
    const count = visibleEntries.length;
    const MAX_COLS = 5;
    const MAX_ROWS = 3;
    return Math.min(MAX_COLS, Math.max(1, Math.ceil(count / MAX_ROWS)));
  }, [visibleEntries.length]);

  // Aggregate deselected statuses into a single "Others" slice so the donut
  // keeps representing the full allowed-status total.
  const othersCount = data
    .filter((entry) => !selectedSet.has(entry.status))
    .reduce((sum, entry) => sum + entry.count, 0);

  const chartData = [
    ...visibleEntries.map((entry) => ({
      name: sanitizeItemStatusLabel(entry.status),
      value: entry.count,
      color: STATUS_COLORS[entry.status] || DEFAULT_COLOR,
    })),
    ...(othersCount > 0
      ? [
          {
            name: t("reports.inventoryScan.statusBreakdown.others"),
            value: othersCount,
            color: OTHERS_COLOR,
          },
        ]
      : []),
  ];

  const hasData = data.length > 0;

  return (
    <ReportSection
      title={t("reports.inventoryScan.statusBreakdown.title")}
      tooltip={tooltip}
      rightSection={rightSection}
    >
      {!hasData || visibleEntries.length === 0 ? (
        <div className="flex h-48 items-center justify-center">
          <EmptyState title={t("common.noData")} />
        </div>
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Left: donut on top, total card (same width as the donut) underneath. */}
          <div className="flex w-52 shrink-0 flex-col gap-4">
            <div className="h-52 w-52">
              <DonutChart data={chartData} size={200} thickness={55} />
            </div>
            <div
              className="flex w-52 flex-col gap-1 rounded-lg border p-3"
              style={{
                borderColor: "var(--mantine-color-dark-2)",
                backgroundColor: "var(--mantine-color-dark-0)",
              }}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {t("reports.inventoryScan.stats.totalItems")}
                </span>
                {totalTooltip && <InfoTooltip content={totalTooltip} />}
              </div>
              <span
                className="text-xl font-extrabold"
                style={{ color: "var(--mantine-color-dark-7)" }}
              >
                {total.count.toLocaleString()}
              </span>
              <span className="text-xs text-gray-500">
                Est. value:{" "}
                <span className="font-medium text-gray-700">{formatCurrency(total.value)}</span>
              </span>
            </div>
          </div>

          {/* Right: a card per selected status, colour-matched to the donut. */}
          <div className="flex-1">
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(${cardColumns}, minmax(0, 1fr))` }}
            >
              {visibleEntries.map((entry) => (
                <StatCard
                  key={entry.status}
                  label={sanitizeItemStatusLabel(entry.status)}
                  count={entry.count}
                  value={entry.estimatedValue}
                  color={STATUS_COLORS[entry.status] || DEFAULT_COLOR}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </ReportSection>
  );
};

export default StatusOverview;
